// API wire-up removes these fixtures, tagConfig, the scenario param and local state hook.
import type {
    T360ClusterDocument,
    T360ClusterGroup,
    T360ClusterLeaf,
    T360ClusterTree,
    T360LeafTag,
} from './types';

export const LABELS_SCENARIOS = [
    'review', 'not-clustered', 'running', 'failed', 'no-documents',
    'suggesting', 'save-fails', 'rerunning', 'rerun-failed', 'many-clusters',
] as const;
export type LabelsScenario = (typeof LABELS_SCENARIOS)[number];

type LeafTagFields = Pick<T360ClusterLeaf, 'suggestedTags' | 'suggestionReason' | 'tags' | 'tagsConfirmedAt'>;

function main(value: string): T360LeafTag {
    return { key: 'main', value };
}

function facet(key: string, value: string): T360LeafTag {
    return { key, value };
}

function buildSuggestion(suggestedTags: T360LeafTag[], reason: string | null = null): LeafTagFields {
    return { suggestedTags, suggestionReason: reason, tags: [], tagsConfirmedAt: null };
}

function buildConfirmed(tags: T360LeafTag[], suggestedTags: T360LeafTag[] = [], reason: string | null = null): LeafTagFields {
    return { suggestedTags, suggestionReason: reason, tags, tagsConfirmedAt: '2026-11-02T09:00:00Z' };
}

function buildClusterGroup(id: number, position: number, name: string, description: string | null,
    leaves: Omit<T360ClusterLeaf, 'groupId' | 'position'>[], isOther = false): T360ClusterGroup {
    return {
        id, position, name, description, isOther,
        size: leaves.reduce((sum, leaf) => sum + leaf.size, 0),
        leaves: leaves.map((leaf, index) => ({ ...leaf, groupId: id, position: index })),
    };
}

const groups: T360ClusterGroup[] = [
    buildClusterGroup(9101, 0, 'Task cards and job cards', 'Step-by-step maintenance tasks signed off by a mechanic.', [
        {
            id: 9111, size: 62, name: 'Airbus Job Cards',
            description: 'Airbus maintenance tasks with mechanic sign-off blocks.',
            terms: ['job card', 'task ref', 'sign-off', 'zone'],
            ...buildSuggestion(
                [main('job-card'), facet('mro', 'lufthansa-technik')],
                'Each page has an Airbus job card header, a task reference and sign-off blocks.',
            ),
        },
        {
            id: 9112, size: 13, name: 'Standard QAR Procedure Task Cards',
            description: 'Numbered QAR procedures with inspection stamps.',
            terms: ['QAR', 'procedure', 'task card no'],
            ...buildSuggestion([main('task-card')], 'Numbered QAR procedure steps with a task card number and inspector stamp.'),
        },
    ]),
    buildClusterGroup(9102, 1, 'Logs and inspection records', 'Day-to-day records of flights, defects and inspections.', [
        {
            id: 9121, size: 6, name: 'Aircraft Journey and Technical Log',
            description: 'Flight sectors and defects recorded in aircraft technical logs.',
            terms: ['sector', 'block time', 'defect', 'ATL'],
            ...buildSuggestion(
                [main('tech-log'), main('flight-log'), facet('log_type', 'pilot'), facet('operator', 'aer-lingus')],
                'Sector rows with block times, defect entries and a captain signature: a journey log and a tech log in one.',
            ),
        },
        {
            id: 9122, size: 5, name: 'Landing Gear Inspection Records',
            description: 'Inspection checklists for the main and nose landing gear.',
            terms: ['MLG', 'NLG', 'inspection', 'checked by'],
            ...buildSuggestion([main('')], 'Inspection checklists without a form title that matches a document type.'),
        },
    ]),
    buildClusterGroup(9103, 2, 'Release certificates and tags', 'Certificates and tags that release a part or task to service.', [
        {
            id: 9132, size: 9, name: 'EASA Form 1 Certificates',
            description: 'Authorised release certificates with approval references.',
            terms: ['EASA Form 1', 'block 12', 'approval ref'],
            ...buildConfirmed([main('arc'), facet('arc_type', 'form1')]),
        },
        {
            id: 9133, size: 4, name: 'Component Removal Sheets',
            description: 'Component removal records with part and serial numbers.',
            terms: ['removed', 'P/N', 'S/N', 'reason for removal'], ...buildSuggestion([]),
        },
        {
            id: 9131, size: 3, name: 'Serviceable Tag Maintenance Records',
            description: 'Serviceable tags identifying the released part and shop.',
            terms: ['serviceable', 'tag', 'P/N'],
            ...buildConfirmed(
                [main('sl-tag')],
                [main('sl-tag')],
                'Serviceable tags with part, serial and the releasing shop.',
            ),
        },
    ]),
    buildClusterGroup(9104, 3, 'Group 4', null, [
        { id: 9141, size: 7, name: 'Mixed Correspondence', description: null,
            terms: ['dear', 'regards', 'subject'], ...buildConfirmed([]) },
        { id: 9142, size: 2, name: 'Unmatched Scans', description: 'Scanned pages that match no template.',
            terms: ['scan', 'page'], ...buildSuggestion([]) },
    ], true),
];

const manyClustersGroup = buildClusterGroup(
    19000,
    4,
    'Maintenance records',
    'Maintenance tasks grouped for review.',
    Array.from({ length: 155 }, (_, index) => ({
        id: 20001 + index,
        size: 1,
        name: `Maintenance cluster ${String(index + 1).padStart(3, '0')}`,
        description: 'A maintenance task with a signed work record.',
        terms: ['maintenance', 'sign-off'],
        ...(index < 140
            ? buildSuggestion([main('job-card')], 'A maintenance task with sign-off blocks.')
            : buildConfirmed([main('arc')])),
    })),
);

const documentNamePrefixesByLeafId: Record<number, string> = {
    9111: 'Job card 05-51-11 #', 9112: 'QAR task card 12-03 #',
    9121: 'ATL page 441', 9122: 'MLG inspection 2025-03-',
    9132: 'EASA Form 1 F1-229', 9133: 'Removal sheet RS-118 #',
    9131: 'Serviceable tag ST-503', 9141: 'Letter 2024-118 #',
    9142: 'Unmatched scan #',
};

function buildLeafDocuments(leaf: T360ClusterLeaf, groupPosition: number): T360ClusterDocument[] {
    let startPage = 1;
    return Array.from({ length: leaf.size }, (_, index) => {
        const pageCount = [1, 2, 4][index % 3];
        const document: T360ClusterDocument = {
            id: leaf.id * 100 + index + 1,
            name: `${documentNamePrefixesByLeafId[leaf.id] ?? `${leaf.name} #`}${index + 1}`,
            fileId: 7000 + groupPosition,
            startPage, endPage: startPage + pageCount - 1, pageCount,
            silhouette: 0.9 - index * 0.01,
            previewUrl: leaf.id === 9111 && index === 0
                ? 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="120" height="160"><rect width="120" height="160" fill="white"/><rect x="12" y="12" width="96" height="16" fill="gray"/></svg>')
                : null,
        };
        startPage += pageCount;
        return document;
    });
}

const reviewGroups = [...groups, manyClustersGroup];
const reviewDocumentCount = reviewGroups.reduce((sum, group) => sum + group.size, 0);

const documentsByLeafId = new Map(reviewGroups.flatMap((group) =>
    group.leaves.map((leaf) => [leaf.id, buildLeafDocuments(leaf, group.position)] as const),
));

const reviewTree: T360ClusterTree = {
    documentCount: reviewDocumentCount,
    run: { id: 412, state: 'done', error: null, createdAt: '2026-11-02T08:14:00Z',
        finishedAt: '2026-11-02T08:31:00Z', stats: {
            docs: reviewDocumentCount,
            groups: reviewGroups.length,
            leaves: reviewGroups.reduce((sum, group) => sum + group.leaves.length, 0),
        } },
    liveRun: null, lastFailedRun: null, labelling: 'done', groups: reviewGroups,
};
const liveRun: NonNullable<T360ClusterTree['liveRun']> = {
    id: 413, state: 'running', createdAt: '2026-11-03T09:02:00Z',
    finishedAt: null, error: null, stats: {},
};
const failedRun: NonNullable<T360ClusterTree['lastFailedRun']> = {
    ...liveRun, state: 'failed', error: 'Clustering needs at least 20 documents with text',
    finishedAt: '2026-11-03T09:05:00Z',
};

export function buildLabelsFixture(scenario: LabelsScenario): {
    tree: T360ClusterTree;
    documentsByLeafId: ReadonlyMap<number, T360ClusterDocument[]>;
} {
    let tree = reviewTree;
    switch (scenario) {
        case 'many-clusters':
            tree = {
                ...reviewTree,
                documentCount: manyClustersGroup.size,
                groups: [manyClustersGroup],
                run: {
                    id: 414, state: 'done', error: null,
                    createdAt: '2026-11-02T08:14:00Z',
                    finishedAt: '2026-11-02T08:31:00Z',
                    stats: { docs: manyClustersGroup.size, groups: 1, leaves: manyClustersGroup.leaves.length },
                },
            };
            break;
        case 'suggesting':
            tree = { ...reviewTree, labelling: 'pending', groups: reviewGroups.map((group) => ({
                ...group, leaves: group.leaves.map((leaf) => [9112, 9121, 9122].includes(leaf.id)
                    ? { ...leaf, ...buildSuggestion([]) } : leaf),
            })) };
            break;
        case 'not-clustered':
        case 'running':
        case 'failed':
        case 'no-documents':
            tree = { ...reviewTree, run: null, groups: [],
                documentCount: scenario === 'no-documents' ? 0 : reviewDocumentCount,
                liveRun: scenario === 'running' ? liveRun : null,
                lastFailedRun: scenario === 'failed' ? failedRun : null };
            break;
        case 'rerunning':
            tree = { ...reviewTree, liveRun };
            break;
        case 'rerun-failed':
            tree = { ...reviewTree, lastFailedRun: failedRun };
            break;
    }
    return { tree, documentsByLeafId };
}
